import { Outcome, Action } from '@agilelab/plugin-wb-builder-common';
import {
  ConfirmDialog,
  WbAlert,
  WbButton,
  WbDrawerActions,
  WbDrawerContent,
} from '@agilelab/plugin-wb-platform';
import { Box, Typography } from '@material-ui/core';
import { FC, useEffect, useMemo, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import { useReleaseDetailPageContext } from '../context/useReleaseDetailPageContext';
import { useDeploymentPreviews } from '../hooks/useDeploymentPreviews';
import { PreviewMessage } from '../types';
import { CleanUpDisclosure } from './CleanUpDisclosure';
import { useDeploymentPreviewContext } from './context/useDeploymentPreviewContext';
import { DeploymentPreviewAdvancedTable } from './DeploymentPreviewAdvancedTable';
import { NotInTargetDescriptorDisclosure } from './NotInTargetDescriptorDisclosure';
import { buildConflictPreviewMessage, createPreviews } from './utils';

export const AdvancedProvisioning: FC = () => {
  const { fetchData, enabled } = useDeploymentPreviews();
  const [{ value: data, loading, error }, loadData] = useAsyncFn(fetchData, [
    fetchData,
  ]);
  const { fetchDeploys, setIsDeploymentPreviewOpen } =
    useReleaseDetailPageContext();

  const {
    setIsAdvanced,
    partialProvision,
    checkPreview,
    previews,
    setPreviews,
    cleanUpPreviews,
    setCleanUpPreviews,
  } = useDeploymentPreviewContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<PreviewMessage | undefined>();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const hasCleanUpOperations = useMemo(() => {
    return Boolean(
      data?.notInTargetDescriptorOperations.length ||
        data?.cleanUpOperations.length,
    );
  }, [data]);

  useEffect(() => {
    if (enabled) loadData();
  }, [enabled, loadData]);

  useEffect(() => {
    if (!loading) {
      const loadedData = [
        ...(data?.rootComponent ? [data.rootComponent] : []),
        ...(data?.previews || []),
      ];
      setPreviews(loadedData);
    }
  }, [loading, data, setPreviews]);

  useEffect(() => {
    if (error)
      setMessage({
        type: 'error',
        title: 'Error',
        content: <Typography>{error.message}</Typography>,
      });
    else setMessage(undefined);
  }, [error]);

  const hasNoActionPreviews = useMemo(() => {
    return previews.some(
      p =>
        p.action === Action.NO_ACTION ||
        p.components?.some(c => c.action === Action.NO_ACTION),
    );
  }, [previews]);

  const runProvision = async () => {
    const newPreviews = createPreviews(previews, cleanUpPreviews);

    setIsLoading(true);
    try {
      const previewResponse = await checkPreview(newPreviews);

      if (previewResponse.outcome !== Outcome.ABORTED_FOR_CONFLICTS) {
        await partialProvision(newPreviews);

        setIsDeploymentPreviewOpen(false);
        setIsAdvanced(false);
        fetchDeploys();
      } else {
        const conflictMessage = buildConflictPreviewMessage(
          previews,
          previewResponse.conflictingOperations ?? [],
        );
        setMessage({
          type: 'error',
          title: 'Error',
          content: <Typography>{conflictMessage}</Typography>,
        });
      }
    } catch (e) {
      const errorList: string[] = [
        ...(e.moreInfo?.problems ?? []),
        ...(e.moreInfo?.solutions ?? []),
      ];
      setMessage({
        type: 'error',
        title: e.message,
        content: (
          <>
            {errorList.map((msg, i) => {
              return <Typography key={i}>{msg}</Typography>;
            })}
          </>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <WbDrawerContent>
        <Box display="flex" flexDirection="column" flex="1" style={{ gap: 8 }}>
          {message && <WbAlert message={message} variant="collapsible" />}

          {!error && (
            <Box style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <DeploymentPreviewAdvancedTable
                setMessage={setMessage}
                loading={loading}
              />

              {hasCleanUpOperations && (
                <Box padding="16px">
                  <Typography style={{ marginBottom: '16px', fontWeight: 500 }}>
                    Clean-up operations
                  </Typography>

                  <NotInTargetDescriptorDisclosure
                    ids={data?.notInTargetDescriptorOperations || []}
                    setCleanUpPreviews={setCleanUpPreviews}
                  />
                  <CleanUpDisclosure
                    ids={data?.cleanUpOperations || []}
                    setCleanUpPreviews={setCleanUpPreviews}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </WbDrawerContent>

      <WbDrawerActions>
        <WbButton
          label="Cancel"
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => {
            setIsAdvanced(false);
            setIsDeploymentPreviewOpen(false);
          }}
        />

        <WbButton
          disabled={Boolean(isLoading || error)}
          loading={isLoading}
          onClick={() => {
            if (hasNoActionPreviews) {
              setConfirmDialogOpen(true);
            } else {
              runProvision();
            }
          }}
          variant="contained"
          size="small"
          color="primary"
          label="Confirm"
        />
      </WbDrawerActions>

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm provisioning"
        description={
          <>
            <Typography style={{ marginBottom: '8px' }}>
              <strong>You selected “NO ACTION” for some components. </strong>
              If a component is already deployed and its target status remains
              “deployed”, this option allows the provisioner to skip
              redeployment{' '}
              <strong>
                only when no changes are detected in its descriptor{' '}
              </strong>
              compared to the last deployment.
            </Typography>
            <Typography>
              <strong>Important:</strong> “NO ACTION” does not guarantee that
              nothing will happen. If any changes are detected, the provisioner{' '}
              <strong>
                will proceed with provisioning the component anyway,
              </strong>{' '}
              overriding this selection. Do you want to continue?
            </Typography>
          </>
        }
        confirmButtonText="Confirm"
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={() => {
          setConfirmDialogOpen(false);
          runProvision();
        }}
      />
    </>
  );
};
